import { BrainCircuit, Compass, MessagesSquare, Route } from "lucide-react";
import Image from "next/image";
import Reveal from "@/components/ui/Reveal";
import {
  darkCard,
  darkCardBody,
  darkCardIcon,
  darkCardLabel,
  darkCardTitle,
} from "./darkCard";

/**
 * Answers the objection as a side-by-side rather than three stacked paragraphs: the point
 * of the copy is a contrast, so the layout makes the contrast the thing you see first.
 * Both cards use the shared dark-card treatment (./darkCard): the section's colour at rest,
 * white on hover.
 */
const columns = [
  {
    key: "chatgpt",
    label: "Generic LLM",
    Icon: MessagesSquare,
    WatermarkIcon: MessagesSquare,
    headline: "An LLM answers the questions people think to ask.",
    body: "It doesn't know what your people already know, what their role requires, or what they should learn next. Every person ends up with a different, unstructured learning experience, and you can't see any of it.",
  },
  {
    key: "inarax",
    label: "inaraX",
    Icon: Route,
    WatermarkIcon: BrainCircuit,
    // The wordmark stands in for the name. Two versions swap on hover: the white-text one on
    // the dark card, the dark-text one once the card turns white. Sized in em so it tracks
    // the heading as it steps from 20px to 24px, and nudged onto the text's baseline.
    headline: (
      <>
        <Image
          src="/images/logo-v2.png"
          alt="inaraX"
          width={784}
          height={318}
          className="inline-block group-hover:hidden h-[1.5em] w-auto align-[-0.45em]"
        />
        <Image
          src="/images/logo.png"
          alt=""
          aria-hidden="true"
          width={1024}
          height={416}
          className="hidden group-hover:inline-block h-[1.5em] w-auto align-[-0.45em]"
        />{" "}
        builds the learning path for each person.
      </>
    ),
    body: "It checks what each person already knows, then adapts expert-built courses to their role, level and goals. When the AI reaches its limit, industry professionals step in. Your L&D team can follow progress across the whole team.",
  },
] as const;

export default function WhyNotChatGpt() {
  return (
    <section
      aria-labelledby="why-not-llm-heading"
      className="relative isolate overflow-hidden bg-mkt-navbar py-size-xl px-margin-mobile md:px-margin-desktop"
    >
      {/* Blurred orb so the dark background reads as lit rather than flat: the same glow as
          TheProblem. Purely decorative. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-[-10%] -z-10 h-[420px] w-[420px] rounded-full bg-mkt-primary/20 blur-[140px]"
      />

      <div className="max-w-[1440px] mx-auto">
        {/* Left-aligned like the other section intros, so the eyebrow drops its mirrored
            trailing rule and keeps the single leading one. No max-width, so the question
            stays on one line wherever the row is wide enough; narrower,
            it wraps into balanced lines. */}
        <Reveal className="flex flex-col items-start text-left mb-size-lg">
          <div className="flex items-center gap-base mb-size-sm">
            <span className="w-12 h-[1px] bg-mkt-primary" />
            <Compass
              aria-hidden="true"
              strokeWidth={1.5}
              className="h-5 w-5 text-white"
            />
            <span className="font-label-md text-label-md text-white uppercase tracking-widest">
              Common question
            </span>
          </div>
          <h2
            id="why-not-llm-heading"
            className="font-display-lg text-[clamp(30px,3.4vw,48px)] leading-[1.15] tracking-[-0.02em] font-bold text-white text-balance"
          >
            Why not just use an LLM?
          </h2>
        </Reveal>

        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-gutter items-stretch">
          {columns.map((column, index) => {
            const { Icon, WatermarkIcon } = column;
            return (
              <Reveal key={column.key} delayMs={index * 100} className="h-full">
                <div
                  className={`${darkCard} relative isolate h-full overflow-hidden p-size-md sm:p-size-lg flex flex-col gap-size-md`}
                >
                  {/* Oversized, mostly-transparent glyph bleeding off the card's corner. */}
                  <WatermarkIcon
                    aria-hidden="true"
                    strokeWidth={0.75}
                    className="pointer-events-none absolute -right-10 -top-10 -z-10 h-44 w-44 text-white/[0.04] transition-all duration-500 group-hover:scale-110 group-hover:text-mkt-primary/10"
                  />

                  <div className="flex items-center justify-between gap-size-sm">
                    <span
                      className={`flex h-14 w-14 items-center justify-center rounded-full ${darkCardIcon}`}
                    >
                      <Icon
                        aria-hidden="true"
                        strokeWidth={1.5}
                        className="h-7 w-7"
                      />
                    </span>
                    <span
                      className={`font-label-md text-label-md uppercase tracking-widest ${darkCardLabel}`}
                    >
                      {column.label}
                    </span>
                  </div>

                  <h3
                    className={`font-headline-lg text-headline-sm md:text-headline-md font-semibold ${darkCardTitle}`}
                  >
                    {column.headline}
                  </h3>
                  <p
                    className={`font-body-lg text-body-md md:text-body-lg ${darkCardBody}`}
                  >
                    {column.body}
                  </p>
                </div>
              </Reveal>
            );
          })}

          {/* Sits in the gutter between the two cards, so it only makes sense once they are
              side by side; below md they stack and it would land mid-card. */}
          <span
            aria-hidden="true"
            className="hidden md:flex absolute left-1/2 top-1/2 z-10 h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-mkt-navbar font-label-md text-label-md uppercase tracking-widest text-white"
          >
            vs
          </span>
        </div>
      </div>
    </section>
  );
}
